import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Collapse, Spinner } from 'react-bootstrap'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { AlwaysScrollToBottom } from '../../../components/AlwaysScrollToBottom/AlwaysScrollToBottom'
import { ErrorEventData, StreamingAIAgentMessage, ToolUse } from '../../../models/StreamingAIAgent'
import '../AIAgentChat.scss'
import { AgentChatInput } from './AgentChatInput'
import { AgentIntro } from './AgentIntro'
import './StreamingAIAgentChat.scss'
import { Button } from '../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { clearChat, selectMessagesForStreaming, selectStreamingError, setStreamingError } from '../../../store/streamingChat/streamingChat'
import { ToolUseNameMap } from '../../../apps/professional-app/fa/MeetingSummary/MeetingSummaryAgent/models'
import { ErrorReasonCodeToMessage } from '../../../apps/professional-app/fa/common/models'
import { MarkdownViewer } from './MarkdownViewer'
import { copyRenderedElementToClipboard } from './utils'

interface StreamingAgentChatProps {
  handleSubmit: (chatMessage: string) => Promise<void>
  handleResubmit: () => Promise<void>
  isLoading: boolean
  disabled?: boolean
  infoText?: string
  header?: React.ReactNode
  collapsible?: boolean
  collapseByDefault?: boolean
  headerOnly?: boolean
  className?: string
}

export const StreamingAIAgentChat: React.FC<StreamingAgentChatProps> = ({
  handleSubmit,
  handleResubmit,
  isLoading,
  disabled,
  infoText,
  header,
  collapsible = false,
  collapseByDefault = false,
  headerOnly = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsible ? collapseByDefault : false)
  const messages = useAppSelector(selectMessagesForStreaming)
  const errorData = useAppSelector(selectStreamingError)
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(clearChat())
    }
  }, [dispatch])

  useEffect(() => {
    setIsCollapsed(headerOnly)
  }, [headerOnly])

  const toggleChat = () => {
    if (collapsible) {
      setIsCollapsed((prev) => !prev)
    }
  }

  const getIntroText = () => {
    if (messages.length > 1) {
      return null
    }

    if (messages.length === 1) {
      return messages[0].type === 'agent' ? messages[0].message : null
    }

    return 'I’m your assistant. I’ll help you get answers from data you don’t have the time or energy to go through.'
  }

  const introText = getIntroText()

  return (
    <div className={`d-flex flex-column border zocks-border-0.5 ${className}`}>
      <div
        className={classNames('d-flex py-4 px-4 align-items-center gap-3', {
          'border-bottom zocks-border-color-gray-200': !isCollapsed,
          'zocks-bg-color-gray-100 zocks-text-gray-600': headerOnly,
        })}
        onClick={toggleChat}
      >
        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-header-logo zocks-border-0.25'>
          <ZocksLogo width={18} height={18} />
        </div>
        <div className='d-flex flex-column'>
          <span className='paragraph-small bold'>ZOCKS</span>
          <span className='paragraph-small'>AI Assistant</span>
        </div>
        {infoText ? (
          <div className='ms-auto'>
            <div className='d-flex ai-agent-chat-header-info-icon align-items-center justify-content-center p-2 zocks-border-0.25'>
              <Tooltip className='ai-agent-chat-header-info-tooltip paragraph-small' title={infoText} placement={'top'} show={true}>
                <i className='bi bi-info-lg' />
              </Tooltip>
            </div>
          </div>
        ) : null}
        {header ? header : null}
        {collapsible ? (
          <i
            className={classNames('bi ms-auto', {
              'bi-chevron-expand': isCollapsed,
              'bi-chevron-contract': !isCollapsed,
            })}
          />
        ) : null}
      </div>
      <Collapse in={!isCollapsed} dimension={'height'} className='flex-grow-1'>
        <div>
          <div className='d-flex flex-column justify-content-between h-100 p-3'>
            {introText ? (
              <AgentIntro introText={introText} />
            ) : (
              <StreamingAgentChatMessages messages={messages} isLoading={isLoading} errorData={errorData} handleResubmit={handleResubmit} />
            )}
            <AgentChatInput handleSubmit={handleSubmit} isLoading={isLoading} disabled={disabled} />
          </div>
        </div>
      </Collapse>
    </div>
  )
}

interface StreamingAgentChatMessagesProps {
  messages: StreamingAIAgentMessage[]
  isLoading: boolean
  errorData: ErrorEventData | null
  handleResubmit: () => Promise<void>
}

const _DEFAULT_LOADING_MESSAGE = 'Thinking…'
const _LONG_LOADING_MESSAGE = "This one's taking extra care. Almost ready…"

const StreamingAgentChatMessages: React.FC<StreamingAgentChatMessagesProps> = ({ messages, isLoading, errorData, handleResubmit }) => {
  const [loadingMessage, setLoadingMessage] = useState(_DEFAULT_LOADING_MESSAGE)
  const [prevIsLoading, setPrevIsLoading] = useState(isLoading)

  const messageBodyRefs = useRef<Map<number, HTMLDivElement | null>>(new Map())

  if (prevIsLoading !== isLoading) {
    if (isLoading) {
      setTimeout(() => {
        setLoadingMessage(_LONG_LOADING_MESSAGE)
      }, 15000)
    } else {
      setLoadingMessage(_DEFAULT_LOADING_MESSAGE)
    }
    setPrevIsLoading(isLoading)
  }

  return (
    <div className='d-flex flex-column zocks-gap-1.25 ai-agent-chat-messages flex-grow-1'>
      {messages.map((message, idx) => {
        if ((idx === 0 && message.type === 'agent') || (!message.message && !message.toolUses?.length)) {
          return null
        }
        if (message.type === 'user') {
          return (
            <div key={idx} className='d-flex px-2 justify-content-end'>
              <div className='zocks-p-0.75 ai-agent-chat-message zocks-border-0.5 zocks-bg-color-primary-light'>{message.message}</div>
            </div>
          )
        }
        const isPreviousMessageUser = idx > 0 && messages[idx - 1].type === 'user'
        const isNextMessageUser = idx + 1 < messages.length && messages[idx + 1].type === 'user'
        const isLastMessage = idx + 1 === messages.length

        const hasCopyButton = (isLastMessage && !isLoading) || isNextMessageUser

        return (
          <div key={idx} className='d-flex flex-column zocks-gap-0.5 px-2'>
            <div className='d-flex zocks-gap-0.75'>
              {isPreviousMessageUser ? (
                <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
                  <ZocksLogo width={12} height={12} />
                </div>
              ) : (
                <div className='ai-agent-chat-message-spacer' />
              )}
              <div className={`d-flex flex-grow-1 flex-column gap-3 ${isLastMessage ? 'mb-1' : ''}`}>
                {message.message ? (
                  <div className='ai-agent-chat-message paragraph-medium'>
                    <div ref={(node) => messageBodyRefs.current.set(idx, node)}>
                      <MarkdownViewer markdown={message.message} />
                    </div>
                    {hasCopyButton ? (
                      <div className={'mt-2'}>
                        <CopyButton onClick={() => copyRenderedElementToClipboard(messageBodyRefs.current.get(idx) ?? null)} />
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {message.toolUses?.length ? <ToolUseBox toolUses={message.toolUses} shouldBeCollapsed={idx !== messages.length - 1} /> : null}
              </div>
            </div>
          </div>
        )
      })}
      {errorData ? <ErrorBox errorData={errorData} handleResubmit={handleResubmit} /> : null}
      {isLoading ? (
        <div className='d-flex zocks-gap-0.75 px-2'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
            <ZocksLogo width={12} height={12} />
          </div>
          <span className='paragraph-medium zocks-text-gray-500 m-0 shimmering-loader-text'>{loadingMessage}</span>
        </div>
      ) : null}
      <AlwaysScrollToBottom dependencies={[messages, errorData]} />
    </div>
  )
}

interface ToolUseBoxProps {
  toolUses: ToolUse[]
  shouldBeCollapsed: boolean
}

const DEFAULT_TOOL_USE_MESSAGE = 'Loading additional data…'

const ToolUseBox: React.FC<ToolUseBoxProps> = ({ toolUses, shouldBeCollapsed }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(shouldBeCollapsed)

  useEffect(() => {
    setIsCollapsed(shouldBeCollapsed)
  }, [shouldBeCollapsed])

  return (
    <div className='d-flex flex-row px-3 py-3 zocks-bg-color-gray-100 zocks-border-color-gray-200 border zocks-border-0.75'>
      <div className='d-flex flex-column zocks-gap-0.5 flex-fill'>
        <div className='paragraph-medium'>Processed data and actions</div>
        <Collapse in={!isCollapsed} dimension={'height'}>
          <div>
            <div className='d-flex flex-column zocks-gap-0.25'>
              {toolUses.map((tool, toolIdx) => (
                <div key={toolIdx} className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-700'>
                  {tool.status === 'running' ? <Spinner className='xs-spinner' /> : <i className='bi bi-check-lg zocks-text-success' />}
                  <span>{ToolUseNameMap[tool.name] ?? DEFAULT_TOOL_USE_MESSAGE}</span>
                </div>
              ))}
            </div>
          </div>
        </Collapse>
      </div>
      <div>
        <i
          className={classNames('bi cursor-pointer', {
            'bi-chevron-down': isCollapsed,
            'bi-chevron-up': !isCollapsed,
          })}
          onClick={() => setIsCollapsed((prev) => !prev)}
        />
      </div>
    </div>
  )
}

interface ErrorBoxProps {
  errorData: ErrorEventData
  handleResubmit: () => Promise<void>
}

const DEFAULT_ERROR_MESSAGE = 'Something happened. Please try again'

const ErrorBox: React.FC<ErrorBoxProps> = ({ errorData, handleResubmit }) => {
  const dispatch = useAppDispatch()

  const resendMessageAndClearError = useCallback(() => {
    dispatch(setStreamingError(null))
    void handleResubmit()
  }, [dispatch, handleResubmit])

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='border border-danger-subtle zocks-border-0.75 p-3 zocks-bg-color-danger-background'>
        <div className='zocks-text-danger-200'>{ErrorReasonCodeToMessage[errorData.reasonCode] ?? DEFAULT_ERROR_MESSAGE}</div>
      </div>
      <Button className='retry-button' variant={'grayGhost'} onClick={resendMessageAndClearError}>
        Retry
      </Button>
    </div>
  )
}

interface CopyButtonProps {
  onClick: () => boolean | Promise<boolean>
  onClickTimeoutMs?: number
}

type CopyButtonPhase = 'idle' | 'copied' | 'failed'

const _COPY_BUTTON_APPEARANCES: Record<CopyButtonPhase, [string, string]> = {
  idle: ['bi-copy', 'Copy'],
  copied: ['bi-check-lg', 'Copied'],
  failed: ['bi-x-lg', 'Unable to copy'],
}

const CopyButton: React.FC<CopyButtonProps> = ({ onClick, onClickTimeoutMs = 2000 }) => {
  const [status, setStatus] = useState<CopyButtonPhase>('idle')

  const handleOnClick = async () => {
    if (status !== 'idle') {
      return
    }
    try {
      const ok = await onClick()
      setStatus(ok ? 'copied' : 'failed')
    } finally {
      setTimeout(() => {
        setStatus('idle')
      }, onClickTimeoutMs)
    }
  }

  const [icon, caption] = _COPY_BUTTON_APPEARANCES[status]

  return (
    <Button
      variant={'link'}
      className={'button-link-small zocks-text-primary justify-content-start align-items-end text-decoration-none p-0'}
      onClick={handleOnClick}
    >
      <i className={icon}></i>
      <div className='ms-1'>{caption}</div>
    </Button>
  )
}
